/**
 * CompteResultat.jsx - calcul 100% backend
 * Le composant ne fait qu'afficher les données reçues.
 */

import React, { useEffect } from "react";
import { useGetCompteResultatQuery } from "actions/comptabilite/queries";
import {
    exportCompteResultatExcel,
    exportCompteResultatPdf,
} from "./utils/exportComptaExcel";
import {
    exportButtonGroupStyle,
    exportExcelButtonStyle,
    exportPdfButtonStyle,
} from "./utils/exportButtonStyles";
import TableLoader from "components/Loader/TableLoader";

const fmt = (v) => {
    if (!v || v === 0) return "-";
    return Math.abs(v).toLocaleString("fr-FR");
};

const COLOR_HEADER = "#009688";
const COLOR_SUBTOTAL = "#009688";

const CompteResultat = ({ dossier_id, date_debut, date_fin }) => {
    const { data, isLoading, error, refetch } = useGetCompteResultatQuery(
        { dossier_id, date_debut, date_fin },
        { skip: !dossier_id }
    );

    useEffect(() => {
        if (dossier_id) refetch();
    }, [dossier_id, date_debut, date_fin]);

    const annee = new Date().getFullYear();

    if (!dossier_id) return null;

    if (isLoading) return (
        <div style={{ background: "#fff", borderRadius: 12, padding: 30, border: "1px solid #c8d8db", margin: "20px auto", maxWidth: 1120 }}>
            <TableLoader
                message="Calcul du compte de résultat..."
                subMessage="Agrégation des charges (Classe 6) et produits (Classe 7)"
                minHeight="350px"
            />
        </div>
    );

    if (error) return (
        <div style={{ textAlign: "center", padding: 20, color: "#DC2626",
                      backgroundColor: "#FEE2E2", borderRadius: 8 }}>
            Impossible de charger le compte de résultat.
        </div>
    );

    const lignes      = data?.lignes      || [];
    const resultatNet = data?.resultat_net ?? 0;
    const handleExportExcel = () => {
        exportCompteResultatExcel({
            lignes,
            resultatNet,
            dossierId: dossier_id,
            annee,
        });
    };
    const handleExportPdf = () => {
        exportCompteResultatPdf({
            lignes,
            resultatNet,
            dossierId: dossier_id,
            annee,
        });
    };

    return (
        <div style={{ width: "100%", padding: "8px" }}>

            {/* Indicateur résultat net + export */}
            <div className="compta-header-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, alignItems: "center", gap: 12 }}>
                <div className="compta-title-wrap" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <h2 style={{ margin: 0, fontSize: "1.35rem", color: "#0f172a" }}>Compte de Résultat</h2>
                    <div style={exportButtonGroupStyle} className="compta-export-group">
                        <button type="button" onClick={handleExportExcel} style={exportExcelButtonStyle}>
                            Export Excel
                        </button>
                        <button type="button" onClick={handleExportPdf} style={exportPdfButtonStyle}>
                            Export PDF
                        </button>
                    </div>
                </div>
                <span className="compta-status-pill" style={{
                    fontSize: 13, fontWeight: 700,
                    padding: "4px 16px", borderRadius: 20,
                    backgroundColor: resultatNet >= 0 ? "#D1FAE5" : "#FEE2E2",
                    color:           resultatNet >= 0 ? "#065F46" : "#DC2626",
                    border: `1px solid ${resultatNet >= 0 ? "#6EE7B7" : "#FCA5A5"}`,
                }}>
                    Résultat net : {resultatNet >= 0 ? "+" : ""}{resultatNet.toLocaleString("fr-FR")} FCFA
                </span>
            </div>

            <div style={{ overflowX: "auto" }}>
                <table style={{
                    width: "100%", minWidth: 860, borderCollapse: "collapse",
                    fontSize: 11, border: `2px solid ${COLOR_HEADER}`,
                }}>
                    <thead>
                        <tr style={{ backgroundColor: "#f8fafc", position: "sticky", top: 0, zIndex: 10 }}>
                            {[
                                { label: "REF",    width: 50,  align: "center" },
                                { label: "LIBELLÉS",           align: "left"   },
                                { label: "SIGNE",  width: 50,  align: "center" },
                                { label: "NOTE",   width: 50,  align: "center" },
                                { label: `31/12/${annee}`,     width: 120, align: "right" },
                                { label: `31/12/${annee - 1}`, width: 120, align: "right" },
                            ].map((h, i) => (
                                <th key={i} style={{
                                    border: `1px solid ${COLOR_HEADER}`,
                                    padding: "6px 8px",
                                    fontWeight: 700,
                                    textAlign: h.align,
                                    width: h.width,
                                    backgroundColor: COLOR_HEADER,
                                    color: "#000",
                                }}>
                                    {h.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {lignes.map((item, i) => {
                            const isTotal = item.isSubtotal;
                            const bg      = isTotal ? COLOR_SUBTOTAL : "#fff";
                            const color   = isTotal ? "#fff"          : "#1f2937";
                            const weight  = isTotal ? "700"            : "400";
                            const border  = `1px solid ${isTotal ? COLOR_HEADER : "#94a3b8"}`;

                            return (
                                <tr key={i} style={{ backgroundColor: bg }}>
                                    <td style={{ border, padding: "4px 8px", textAlign: "center", color, fontWeight: weight }}>
                                        {item.ref}
                                    </td>
                                    <td style={{ border, padding: "4px 8px", color, fontWeight: weight }}>
                                        {item.libelle}
                                    </td>
                                    <td style={{ border, padding: "4px 8px", textAlign: "center", color, fontWeight: weight }}>
                                        {item.sign}
                                    </td>
                                    <td style={{ border, padding: "4px 8px", textAlign: "center", color }}>
                                        {item.note || ""}
                                    </td>
                                    <td style={{ border, padding: "4px 8px", textAlign: "right", fontFamily: "monospace", color, fontWeight: weight }}>
                                        {fmt(item.netN)}
                                    </td>
                                    <td style={{ border, padding: "4px 8px", textAlign: "right", fontFamily: "monospace", color, fontWeight: weight }}>
                                        {fmt(item.netN1)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CompteResultat;